import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileCheck2, ArrowRight, Building2, FlaskConical } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Stagger, itemVariant, Reveal } from "../components/Reveal";
import { LAB_TESTS, IMAGES } from "../data/content";

export default function Quality() {
  const { t, lang, pick } = useLang();
  return (
    <>
      <Seo title="Qualität & Labor – MFI, DSC, Shore, Rückverfolgbarkeit | PlastoNord"
        description="Eigenes Labor von PlastoNord: MFI, DSC, Spektrometrie, Feuchtigkeitsmessung, Shore-Härte, Schlag- und Zugprüfung. Plus 100+ Prüfungen über Partnerlabor. Dokumentierte Qualität mit Chargenrückverfolgbarkeit."
        path="/qualitaet" />
      <PageHero overline={t("nav.quality")}
        title={lang === "de" ? "Geprüfte Qualität aus dem eigenen Labor" : "Tested quality from our own laboratory"}
        subtitle={lang === "de" ? "Jede Charge ist dokumentiert und rückverfolgbar – für reproduzierbare Verarbeitung und volle Sicherheit." : "Every batch is documented and traceable – for reproducible processing and full confidence."} />
      <section className="py-24">
        <div className="pn-container grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Reveal>
              <p className="overline">{lang === "de" ? "Im eigenen Haus" : "In-house"}</p>
              <h2 className="mt-2 font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "7 Prüfungen in unserem eigenen Labor" : "7 tests in our own laboratory"}</h2>
              <p className="mt-2 mb-7 max-w-xl text-sm text-[#4a4d52]">{lang === "de" ? "Klicken Sie auf eine Prüfung für Details: was gemessen wird, welche Daten Sie erhalten und warum sie für Ihre Anwendung wichtig ist." : "Click a test for details: what is measured, which data you receive and why it matters for your application."}</p>
            </Reveal>
            <Stagger className="grid gap-5 sm:grid-cols-2">
              {LAB_TESTS.map((test) => { const c = pick(test); return (
                <motion.div key={test.slug} variants={itemVariant}>
                  <Link to={`/qualitaet/${test.slug}`} data-testid={`quality-test-${test.slug}`} className="pn-card group flex h-full flex-col p-6">
                    <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><FileCheck2 size={18} /></span>
                    <h3 className="mt-4 font-head text-base font-semibold text-[#16181D] group-hover:text-[#E2611C]">{c.t}</h3>
                    <p className="mt-1.5 flex-1 text-sm text-[#4a4d52]">{c.d}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#E2611C]">{t("cta.details")} <ArrowRight size={14} /></span>
                  </Link>
                </motion.div>
              ); })}
            </Stagger>
          </div>
          <Reveal delay={0.1}>
            <img src={IMAGES.lab} alt="Qualitätskontrolle im Labor von PlastoNord" className="rounded-2xl border border-[#e4e9f0] shadow-xl" loading="lazy" />
            <div className="mt-6 rounded-2xl bg-[#16181D] p-7 text-white">
              <h3 className="font-head text-xl font-medium">{lang === "de" ? "Chargenrückverfolgbarkeit" : "Batch traceability"}</h3>
              <p className="mt-2 text-sm text-white/70">{lang === "de" ? "Jede Lieferung wird mit Charge, Prüfwerten und Zertifikaten (COA) dokumentiert – digital im Kundenportal abrufbar." : "Every delivery is documented with batch, test values and certificates (COA) – digitally available in the customer portal."}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* In-house + partner lab capability */}
      <section className="bg-[#f6f8fb] py-20">
        <div className="pn-container grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="pn-card h-full p-8">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#16181D] text-white"><FlaskConical size={22} /></span>
              <h2 className="mt-5 font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "Eigenes Labor – 7 Standardprüfungen" : "Own lab – 7 standard tests"}</h2>
              <p className="mt-3 leading-relaxed text-[#4a4d52]">{lang === "de"
                ? "Die oben genannten sieben Prüfungen führen wir intern in unserem eigenen Haus mit Hilfe unseres Labors durch – schnell, zuverlässig und mit dokumentierten Ergebnissen für jede Charge."
                : "We perform the seven tests above internally in our own house with our laboratory – fast, reliable and with documented results for every batch."}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="pn-card h-full p-8 ring-1 ring-[#E2611C]/30">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#E2611C] text-white"><Building2 size={22} /></span>
              <h2 className="mt-5 font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "100+ Prüfungen über unser Partnerlabor" : "100+ tests via our partner lab"}</h2>
              <p className="mt-3 leading-relaxed text-[#4a4d52]">{lang === "de"
                ? "Darüber hinaus können wir mit Hilfe unserer Partnerfirma über 100 weitere Prüfungen durchführen. Dabei erstellen wir bei Bedarf Laborberichte von mehreren hundert Seiten und führen genau die Prüfungen durch, die für Sie als Kunde relevant sind."
                : "In addition, together with our partner company we can perform over 100 further tests. We create lab reports of several hundred pages where required and run exactly the tests that are relevant to you as a customer."}</p>
              <Link to="/kontakt" className="btn-primary mt-6 !px-5 !py-2 text-sm" data-testid="quality-partner-cta">{lang === "de" ? "Prüfung anfragen" : "Request a test"} <ArrowRight size={15} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

